
import React from 'react';
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { 
  BanknotesIcon, 
  HistoryIcon 
} from '../BorrowingsIcons';
import { formatCurrency, formatDate } from './utils';

interface LoansListProps {
  viewType: "active" | "past";
  setViewType: (type: "active" | "past") => void;
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  statusFilter: string;
  setStatusFilter: (filter: string) => void;
  typeFilter: string;
  setTypeFilter: (filter: string) => void;
  filteredLoans: any[];
  selectedLoan: number | null;
  setSelectedLoan: (id: number | null) => void;
  handleViewDetails: (loan: any) => void;
}

export const LoansList: React.FC<LoansListProps> = ({
  viewType,
  setViewType,
  searchQuery,
  setSearchQuery,
  statusFilter,
  setStatusFilter,
  typeFilter,
  setTypeFilter,
  filteredLoans,
  selectedLoan,
  setSelectedLoan,
  handleViewDetails
}) => {
  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row gap-4">
        <Tabs value={viewType} onValueChange={(v) => setViewType(v as "active" | "past")} className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="active" className="flex items-center gap-2">
              <BanknotesIcon className="h-4 w-4" />
              Active Borrowings
            </TabsTrigger>
            <TabsTrigger value="past" className="flex items-center gap-2">
              <HistoryIcon className="h-4 w-4" />
              Past Borrowings
            </TabsTrigger>
          </TabsList>
        </Tabs>
      </div>
      
      <div className="flex flex-col sm:flex-row gap-4">
        <Input
          placeholder="Search by instrument type or lender..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="max-w-md"
        />
        <div className="flex gap-4">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[150px]">
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="closed">Closed</SelectItem>
            </SelectContent>
          </Select>
          
          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Filter by type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              <SelectItem value="term">Term Loan</SelectItem>
              <SelectItem value="working">Working Capital</SelectItem>
              <SelectItem value="debenture">Debenture</SelectItem>
              <SelectItem value="commercial">Commercial Paper</SelectItem>
              <SelectItem value="external">ECB</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      
      <div className="w-full">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Instrument</TableHead>
              <TableHead>Lender</TableHead>
              <TableHead className="text-right">Amount</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredLoans.length > 0 ? (
              filteredLoans.map((loan) => (
                <TableRow 
                  key={loan.id} 
                  className={`${selectedLoan === loan.id ? 'bg-muted' : ''}`}
                  onClick={() => setSelectedLoan(loan.id)}
                >
                  <TableCell className="font-medium">
                    {loan.instrumentType}
                    <div className="text-xs text-muted-foreground">{formatDate(loan.sanctionDate)}</div>
                  </TableCell>
                  <TableCell>{loan.lender}</TableCell>
                  <TableCell className="text-right">
                    {formatCurrency(loan.amount, loan.currency)}
                    <div className="text-xs text-muted-foreground">{loan.interestRate}% {loan.interestType}</div>
                  </TableCell>
                  <TableCell className="text-right">
                    <button 
                      className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 border border-input bg-background hover:bg-accent hover:text-accent-foreground h-9 rounded-md px-3"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleViewDetails(loan);
                      }}
                    >
                      View Details
                    </button>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={4} className="text-center py-4">
                  No {viewType} borrowings found
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};
